import PackageIcon from "@redotech/redo-icons/package.svg";
import { Order } from "@redotech/redo-model/order";
import { ReturnType } from "@redotech/redo-model/return";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { getDateString } from "@redotech/util/date-format";
import { memo } from "react";
import * as styles from "./order-search-result-card.module.css";

function isRedoLineItem(lineItem: any): boolean {
  return lineItem.vendor === "re:do";
}

function getOrderItemCount(order: Order): number {
  return order.shopify.line_items
    .filter((item) => !isRedoLineItem(item))
    .reduce((sum, item) => sum + item.quantity, 0);
}

export const OrderSearchResultCard = memo(function OrderSearchResultCard({
  order,
  returnType,
  onClick,
  hasReturnableItems,
}: {
  order: Order;
  returnType: ReturnType;
  onClick: () => void;
  hasReturnableItems?: boolean;
}) {
  const itemCount = getOrderItemCount(order);
  const formattedDate = getDateString(new Date(order.createdAt));

  const isProtected =
    returnType === "claim" ? order.packageProtected : order.protected;
  const handleClick = () => {
    onClick();
  };

  return (
    <div className={styles.orderSearchResultCard} onClick={handleClick}>
      <Flex align="center" dir="row" gap="md">
        <RedoIcon color="primary" Icon={PackageIcon} size={16} />
        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" dir="row" gap="sm">
            <Text fontSize="sm" fontWeight="semibold">
              {order.shopify.name}
            </Text>
            <RedoBadge
              color={isProtected ? "purple" : "gray"}
              size="xs"
              text={isProtected ? "Protected" : "Not protected"}
            />
            {hasReturnableItems === false && (
              <RedoBadge
                color="error"
                size="xs"
                text={`No items available for ${returnType === "warranty" ? "warranty claim" : returnType}`}
              />
            )}
          </Flex>
          <Flex align="center" dir="row" gap="xs">
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              {order.customer_name}
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              •
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              {formattedDate}
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              •
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
});

export const OrderSearchResultSkeleton = memo(
  function OrderSearchResultSkeleton() {
    return (
      <div className={styles.skeletonCard}>
        <Flex align="center" dir="row" gap="md">
          <AnimatedSkeletonBlock height="40px" width="40px" />
          <Flex dir="column" gap="sm" w="full">
            <Flex align="center" dir="row" gap="sm">
              <AnimatedSkeletonBlock height="20px" width="80px" />
              <AnimatedSkeletonBlock height="24px" width="100px" />
            </Flex>
            <AnimatedSkeletonBlock height="16px" width="60%" />
          </Flex>
        </Flex>
      </div>
    );
  },
);
